/**
 * Normalises a context field value so that "no selection" and
 * "all options selected" are treated as equivalent.
 *
 * Returns `null` to represent "everything" (no filter active),
 * or a sorted array of specific selections.
 */
function normalizeField(
  selected: string[] | undefined,
  allOptions: string[] | undefined
): string[] | null {
  // No selection → "everything".
  if (!selected || selected.length === 0) return null;

  // All options selected → also "everything".
  if (
    allOptions &&
    allOptions.length > 0 &&
    selected.length >= allOptions.length &&
    allOptions.every((option) => selected.includes(option))
  ) {
    return null;
  }

  return [...selected].sort();
}

function fieldsEqual(
  current: string[] | null,
  applied: string[] | null
): boolean {
  if (current === null && applied === null) return true;
  if (current === null || applied === null) return false;
  if (current.length !== applied.length) return false;
  return current.every((value, index) => value === applied[index]);
}

/** A single named value to compare (for non-context fields like producer, version, year, dims). */
export interface ScalarField {
  name: string;
  current: string | undefined;
  applied: string | undefined;
}

/** A context field with available base options (for the "no selection = all" normalisation). */
export interface ContextFieldComparison {
  name: string;
  current: string[] | undefined;
  applied: string[] | undefined;
  allOptions: string[] | undefined;
}

/**
 * Compares the current UI selections against what was last applied/loaded.
 *
 * Handles the "no selection = all available" equivalence for context fields.
 * Returns which fields have changed so the UI can show what's out of sync.
 */
export function getContextSyncStatus(
  contextFields: ContextFieldComparison[],
  scalarFields: ScalarField[] = []
): { isDirty: boolean; changedFields: string[] } {
  const changed: string[] = [];

  for (const { name, current, applied, allOptions } of contextFields) {
    const normCurrent = normalizeField(current, allOptions);
    const normApplied = normalizeField(applied, allOptions);
    if (!fieldsEqual(normCurrent, normApplied)) {
      changed.push(name);
    }
  }

  for (const { name, current, applied } of scalarFields) {
    if ((current ?? "") !== (applied ?? "")) {
      changed.push(name);
    }
  }

  return { isDirty: changed.length > 0, changedFields: changed };
}
